import http from "./http-common";
import JWT from "./Jwt";
import Crypto from "./Crypto"
import { getServidor, getServidorPorAmbiente } from "../utils/Utils";

/**
 * @class PosicionesDataService
 * @brief Gestiona las operaciones de acceso a los datos de posiciones.
 */
class PosicionesDataService {

    /**
     * Endpoint de Posiciones
     */

    /**
     * @brief Obtiene todas las posiciones asociadas a un área.
     *
     * @param data Datos que identifican el área de las posiciones que se desean obtener.
     * @param key Clave utilizada para realizar la operación de encriptación.
     *
     * @returns {Promise<AxiosResponse<any>>} Respuesta de la petición con las posiciones del área.
     */
    async getAll(data, key) {
        const datosEncriptados = await Crypto.encriptar(data, key);
        return http.get(`${getServidor()}/posiciones/last-position-por-usuario/`, {
            params: datosEncriptados,
            showLoader: false
        });
    }

    /**
     * @brief Devuelve si existen posiciones registradas en la BD dado un dispositivo.
     *
     * @param key Clave utilizada para realizar la operación de encriptación.
     *
     * @returns {Promise<AxiosResponse<any>>} Respuesta de la petición con la información sobre existencia de posiciones.
     */
    async getExistenPosicionesByDispositivo(key) {
        const datosEncriptados = await Crypto.encriptar(sessionStorage.getItem('id'), key);
        return http.get(`${getServidor()}/posiciones/dispositivos/posiciones`, {
            params: datosEncriptados
        })
    }

    /**
     * Devuelve la última posición dado un dispositivo
     * @returns {Promise<AxiosResponse<any>>}
     */
    async getPositionByDevice(idDispositivo, key) {
        const datosEncriptados = await Crypto.encriptar(idDispositivo, key);
        return http.get(`${getServidor()}/posiciones/last-position-device/`, {
            params: datosEncriptados
        });
    }

    /**
     * @brief Devuelve la última posición de cada dispositivo dado un array de ids de dispositivos.
     *
     * @param idDispositivo Identificadores de los dispositivos cuya última posición se desea obtener.
     * @param key Clave utilizada para realizar la operación de encriptación.
     *
     * @returns {Promise<AxiosResponse<any>>} Respuesta de la petición con las últimas posiciones de los dispositivos.
     */
    async getPositionByDevicesId(idDispositivo, key) {
        const datosEncriptados = await Crypto.encriptar(idDispositivo, key);
        return http.get(`${getServidor()}/posiciones/last-position-devices/`, {
            params: datosEncriptados,
            showLoader: false
        });
    }
    /**
     * @brief Obtiene la última posición de los dispositivos para el enlace compartido.
     *
     * @param idDispositivo Identificador del dispositivo cuya posición se desea obtener.
     *
     * @returns {Promise<AxiosResponse<any>>} Respuesta de la petición con la última posición del dispositivo.
     */
    getPositionByDevicesIdCompartirLink(idDispositivo) {
        return http.get(`${getServidorPorAmbiente()}/posiciones/last-position-devices-compartir-link/` + JWT.jwtEncode(idDispositivo), {
            showLoader: false
        });
    }

    /**
     * Historial de posiciones de dispositivo
     * @returns {Promise<AxiosResponse<any>>}
     */
    async getHistory(data, key) {
        const datosEncriptados = await Crypto.encriptar(data, key);
        return http.get(`${getServidor()}/posiciones/dispositivos/historico`, {
            params: datosEncriptados,
            showLoader: false
        });
    }

    /**
     * Historial de posiciones de dispositivo en formato excel
     * @returns {Promise<AxiosResponse<any>>}
     */
    async getHistoryExcel(data, key) {
        const datosEncriptados = await Crypto.encriptar(data, key);
        return http.get(`${getServidor()}/posiciones/dispositivos/descargar`, {
            params: datosEncriptados,
            responseType: 'blob',
            showLoader: false
        });
    }

    /**
     * Historial de posiciones de dispositivo
     * @returns {Promise<AxiosResponse<any>>}
     */
    async getStaticHistory(data, key) {
        const datosEncriptados = await Crypto.encriptar(data, key);
        return http.get(`${getServidor()}/posiciones/dispositivos/usuarios2/historico`, {
            params: datosEncriptados,
            showLoader: false
        });
    }

    /**
     * @brief Permite agregar una posición.
     *
     * @param data Datos de la posición que se desea agregar.
     *
     * @returns {Promise<AxiosResponse<any>>} Respuesta de la petición con el resultado de la operación.
     */
    create(data) {
        return http.post(localStorage.getItem('servidor') + "/asincrona/posiciones/LAC/", JWT.jwtEncodeJson(data));
    }

    /**
     * @brief Consulta los dispositivos a la redonda dado un dispositivo.
     *
     * @param data Datos que identifican el dispositivo y su posición para consultar dispositivos cercanos.
     * @param key Clave utilizada para realizar la operación de encriptación.
     *
     * @returns {Promise<AxiosResponse<any>>} Respuesta de la petición con los dispositivos a la redonda.
     */
    async getDispositivosRedonda(data, key) {
        const datosEncriptados = await Crypto.encriptar(data, key);
        return http.get(`${getServidor()}/posiciones/obtenerDispositivosRedonda/`, {
            params: datosEncriptados
        });
    }
    /**
     * @brief Obtiene los dispositivos asociados a un área determinada.
     *
     * @param data Datos que identifican el área cuyos dispositivos se desean consultar.
     * @param key Clave utilizada para realizar la operación de encriptación.
     *
     * @returns {Promise<AxiosResponse<any>>} Respuesta de la petición con los dispositivos asociados al área.
     */
    async getDispositivosPorArea(data, key) {
        const datosEncriptados = await Crypto.encriptar(data, key);
        return http.get(`${getServidor()}/posiciones/obtenerDispositivosPorArea/`, {
            params: datosEncriptados
        });
    }
    /**
     * @brief Obtiene los dispositivos asociados a un área poligonal determinada.
     *
     * @param data Datos que identifican el área poligonal cuyos dispositivos se desean consultar.
     * @param key Clave utilizada para realizar la operación de encriptación.
     *
     * @returns {Promise<AxiosResponse<any>>} Respuesta de la petición con los dispositivos asociados al área poligonal.
     */
    async getDispositivosPorAreaPoligonal(data, key) {
        const datosEncriptados = await Crypto.encriptar(data, key);
        return http.get(`${getServidor()}/posiciones/obtenerDispositivosPorAreaPoligonal/`, {
            params: datosEncriptados
        });
    }

    /**
     * @brief Obtiene el total de posiciones registradas de un dispositivo en un rango de fechas determinado.
     *
     * @param data Datos que identifican el dispositivo y el rango de fechas para contar las posiciones.
     * @param key Clave utilizada para realizar la operación de encriptación.
     *
     * @returns {Promise<AxiosResponse<any>>} Respuesta de la petición con el total de posiciones del dispositivo.
     */
    async getTotalPosicionesPorRangoFechas(data, key) {
        const datosEncriptados = await Crypto.encriptar(data, key);
        return http.get(`${getServidor()}/posiciones/getTotalPosicionesPorRangoFechas/`, {
            params: datosEncriptados,
            showLoader: false
        });
    }
}

export default new PosicionesDataService();
